/**
 * Cancel-control eligibility (Batch B.1-polish-2). Splits the cancel CTA into VISIBILITY vs ENABLEMENT so
 * the manual escape-hatch is discoverable (always shown for the orders it applies to) yet only clickable
 * when the order is actually cancellable — and never renders a visible-enabled button that the backend
 * would reject. These are pure predicates so the load-bearing gating is unit-testable.
 *
 * Scope: the cancel control applies ONLY to deBridge CROSS-CHAIN orders (which have an on-chain
 * 'Created' deBridge order a solver may not have filled). Jupiter same-chain swaps, deBridge same-chain
 * swaps, and CCTP transfers are atomic / different rails — they show no cancel button.
 *
 * Backend mirror: auto-cancel.ts userCancel cancels ONLY when debridge_orders.order_status === 'Created'.
 * The ENABLED gate here must mirror that exactly (the caller supplies `isCreated` from the authoritative
 * state it has: the live dexOrderStatus.state for the active order, or the 'polling' frontend proxy for a
 * persisted order), so a clickable button never hits a backend "not cancellable".
 */
import { chainFamily } from '@parse/shared'
import type { StoredDexOrder } from '@/utils/dexOrderStorage'

/** The connected wallet address per family (any may be null when that family isn't connected). */
export interface ConnectedAddresses {
  evmAddress?: string | null
  solanaAddress?: string | null
  tronAddress?: string | null
}

/**
 * VISIBILITY: should this order show a cancel control at all? True ONLY for a deBridge CROSS-CHAIN order.
 * Cross-chain (sourceChain !== destinationChain) excludes same-chain swaps; the `debridge` provider prefix
 * excludes Jupiter ('jupiter') and CCTP ('cctp'). deBridge same-chain ('debridge_same') is excluded by the
 * cross-chain check.
 */
export function isDexCancelControl(order: { sourceChain: string; destinationChain: string; provider?: string }): boolean {
  return order.sourceChain !== order.destinationChain && (order.provider ?? '').startsWith('debridge')
}

/** Family-aware owner check: connected wallet === order.source_address (EVM case-insensitive; Tron/Solana exact). */
export function isOrderOwnedByConnected(
  order: { sourceChain: string; sourceAddress: string },
  connected: ConnectedAddresses,
): boolean {
  if (!order.sourceAddress) return false
  const fam = chainFamily(order.sourceChain)
  const wallet = fam === 'tron' ? connected.tronAddress
    : fam === 'solana' ? connected.solanaAddress
    : connected.evmAddress
  if (!wallet) return false
  return fam === 'evm'
    ? wallet.toLowerCase() === order.sourceAddress.toLowerCase()
    : wallet === order.sourceAddress
}

// The cancel control is HIDDEN during the normal fill window (avg < 30s; UI quotes 1–3 min) and only
// appears once an order has been stuck in the cancellable 'Created' state for at least this long — i.e.
// genuinely stuck, not merely mid-fill. Showing a button on stale/normal state would invite a cancel the
// solver is about to make unnecessary (or that races the fill).
export const CANCEL_VISIBLE_AFTER_MS = 60_000

// The only disabled-but-visible state left under the stuck-≥60s model: an in-flight cancel keeps the button
// visible+disabled ("Cancelling…") through the attempt so it doesn't vanish mid-action.
export type CancelDisabledReason = 'in_progress'

export interface DexCancelControl {
  visible: boolean
  enabled: boolean
  reason?: CancelDisabledReason
}

/**
 * Compute the cancel control (visibility + enablement) from explicit lifecycle inputs. The caller supplies
 * the authoritative `isCreated` (live dexOrderStatus.state==='Created' for the active order, or the 'polling'
 * frontend proxy for a persisted order) and `ageMs` (how long the order has existed — used as the stuck
 * proxy).
 *
 *   VISIBLE iff: deBridge cross-chain AND owned AND ( (Created AND stuck ≥ 60s) OR a cancel is in flight ).
 *   ENABLED iff: visible AND Created AND NOT in flight.
 *
 * Everything else (same-chain/Jupiter/CCTP, not owned, not-yet-/no-longer-'Created', or Created < 60s) is
 * NOT visible — under the stuck-only model those are hidden rather than shown disabled. The lone visible-
 * disabled case is the in-flight "Cancelling…" state.
 */
export function dexCancelControl(input: {
  isDebridgeCrossChain: boolean
  isCreated: boolean
  owned: boolean
  cancelInFlight: boolean
  ageMs: number
}): DexCancelControl {
  const stuck = input.isCreated && input.ageMs >= CANCEL_VISIBLE_AFTER_MS
  const visible = input.isDebridgeCrossChain && input.owned && (stuck || input.cancelInFlight)
  if (!visible) return { visible: false, enabled: false }
  if (input.isCreated && !input.cancelInFlight) return { visible: true, enabled: true }
  // visible but not enabled → a cancel is in flight (the only remaining disabled-visible case).
  return { visible: true, enabled: false, reason: 'in_progress' }
}

/** Tooltip/title text for a disabled cancel button (undefined when enabled). */
export function cancelReasonText(reason?: CancelDisabledReason): string | undefined {
  return reason === 'in_progress' ? 'Cancellation in progress…' : undefined
}

/**
 * Convenience for a PERSISTED (history) order row: derive the full control from the stored order + connected
 * wallets. Uses the frontend 'polling' status as the deBridge-'Created' proxy (a cross-chain order confirmed
 * on the source and waiting for a solver fill) and `now − createdAt` as the stuck duration. `cancelInFlight`
 * comes from the per-order cancelStates. `now` is injectable for tests.
 */
export function storedOrderCancelControl(
  order: StoredDexOrder,
  connected: ConnectedAddresses,
  cancelInFlight: boolean,
  now: number = Date.now(),
): DexCancelControl {
  return dexCancelControl({
    isDebridgeCrossChain: isDexCancelControl(order),
    isCreated: order.orderStatus === 'polling',
    owned: isOrderOwnedByConnected(order, connected),
    cancelInFlight,
    ageMs: now - order.createdAt,
  })
}

// deBridge order states that mean the cross-chain order is fulfilled/cancelled/unlocked — no longer 'Created'
// and never cancellable. Listed for clarity; the gate only needs state==='Created' for isCreated.
const DEBRIDGE_TERMINAL_STATES = new Set([
  'Fulfilled', 'OrderCancelled', 'SentUnlock', 'ClaimedUnlock', 'ClaimedOrderCancel',
])

/**
 * Convenience for the ACTIVE (in-flight) order row: derive the control from the LIVE deBridge state. Only
 * `state === 'Created'` is cancellable; any terminal/unlock state (or null/unknown) → not Created → hidden
 * unless a cancel is mid-flight. `ageMs` is `now − the order's createdAt`. Pure, so the live-state→control
 * mapping is unit-tested (not only the manual gate).
 */
export function activeOrderCancelControl(input: {
  debridgeState: string | null
  isDebridgeCrossChain: boolean
  owned: boolean
  cancelInFlight: boolean
  ageMs: number
}): DexCancelControl {
  return dexCancelControl({
    isDebridgeCrossChain: input.isDebridgeCrossChain,
    isCreated: input.debridgeState === 'Created' && !DEBRIDGE_TERMINAL_STATES.has(input.debridgeState),
    owned: input.owned,
    cancelInFlight: input.cancelInFlight,
    ageMs: input.ageMs,
  })
}

/**
 * Assemble the ACTIVE (in-flight) row's cancel control from the page's live inputs — the whole glue that
 * was previously inline in BridgePage's memo, extracted so the 60s-reveal logic is unit-testable (a future
 * "optimize the memo dep back to .state" regression would otherwise silently break the reveal with no red
 * test). The stuck clock is `now − storedOrder.createdAt`; calling this twice with `now` advanced past 60s
 * is exactly what the ~5s order-status poll does — the second call flips visible from false to true.
 *
 * Returns undefined when there is no persisted order yet (nothing to cancel / no source_address to own).
 */
export function buildActiveDexCancelControl(input: {
  dexOrderStatus: { state?: string | null } | null | undefined
  storedOrder: StoredDexOrder | undefined
  connectedWallets: ConnectedAddresses
  cancelInFlight: boolean
  now: number
}): DexCancelControl | undefined {
  const { storedOrder } = input
  if (!storedOrder) return undefined
  return activeOrderCancelControl({
    debridgeState: input.dexOrderStatus?.state ?? null,
    isDebridgeCrossChain: isDexCancelControl(storedOrder),
    owned: isOrderOwnedByConnected(storedOrder, input.connectedWallets),
    cancelInFlight: input.cancelInFlight,
    ageMs: input.now - storedOrder.createdAt,
  })
}
